import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Clock, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

function buildTree(flatItems) {
  const map = {}; const roots = [];
  flatItems.forEach(item => { map[item.id] = { ...item, children: [] }; });
  flatItems.forEach(item => {
    if (item.parent_id && map[item.parent_id]) map[item.parent_id].children.push(map[item.id]);
    else roots.push(map[item.id]);
  });
  const sort = (nodes) => { nodes.sort((a, b) => a.display_order - b.display_order); nodes.forEach(n => sort(n.children)); };
  sort(roots);
  return roots;
}

function TreeRow({ node, depth }) {
  if (node.visible === false) return null;
  const isGroup = node.item_type === 'group';
  const val = Number(node.result_value || 0);
  const bold = isGroup || node.children.length > 0;
  return (
    <>
      <tr className={isGroup ? 'bg-indigo-50' : 'bg-white'}>
        <td className="py-2 px-3 border-b border-gray-100" style={{ paddingLeft: `${depth * 24 + 12}px` }}>
          <span className={bold ? 'font-semibold text-gray-800' : 'text-gray-700'}>{node.name}</span>
          {node.description && <div className="text-xs text-gray-400 mt-0.5">{node.description}</div>}
        </td>
        <td className={`py-2 px-3 border-b border-gray-100 text-right ${bold ? 'font-bold text-gray-800' : 'text-gray-600'}`}>
          {val > 0 ? val.toLocaleString('pl-PL', { minimumFractionDigits: 2 }) : ''}
        </td>
      </tr>
      {node.children.filter(c => c.visible !== false).map(child => (
        <TreeRow key={child.id} node={child} depth={depth + 1} />
      ))}
    </>
  );
}

export default function PublicOfferPage() {
  const { t, i18n } = useTranslation();
  const toggleLang = () => { const next = i18n.language === 'pl' ? 'en' : 'pl'; i18n.changeLanguage(next); localStorage.setItem('swibi_language', next); };
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/offers/public/${token}`)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.status === 410 ? 'expired' : 'notfound'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-400 text-sm">{t('publicOffer.loadingOffer')}</div>
    </div>
  );

  if (error === 'expired') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <Clock size={48} className="text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">{t('publicOffer.linkExpiredTitle')}</h2>
        <p className="text-gray-500">{t('publicOffer.linkExpiredMessage')}</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">{t('publicOffer.offerUnavailableTitle')}</h2>
        <p className="text-gray-500">{t('publicOffer.offerUnavailableMessage')}</p>
      </div>
    </div>
  );

  const { offer, items, variants, collaborators, local_variables } = data;
  const localVars = local_variables || [];
  const company = offer.company_name || offer.client_company || '';
  const issuer = offer.issuer_name || '';
  const issuerAddress = [offer.issuer_street, offer.issuer_zip, offer.issuer_city].filter(Boolean).join(', ');
  const priceType = offer.price_type || 'netto';
  const vatRate = Number(offer.vat_rate ?? 23);
  const cur = offer.currency || 'PLN';
  const fmt = v => v.toLocaleString('pl-PL', { minimumFractionDigits: 2 });
  const visibleVariants = (variants || []).filter(v => v.visible !== false);
  const total = visibleVariants.reduce((sum, v) => {
    return sum + items.filter(i => i.variant_id === v.id && !i.parent_id).reduce((s, i) => s + Number(i.result_value || 0), 0);
  }, 0);
  let netto, brutto, vatAmount;
  if (priceType === 'netto') {
    netto = total; vatAmount = total * vatRate / 100; brutto = total + vatAmount;
  } else {
    brutto = total; vatAmount = total - total / (1 + vatRate / 100); netto = total - vatAmount;
  }

  const authors = [
    { name: offer.creator_name, job_title: offer.creator_job_title, email: offer.creator_contact_email || offer.creator_email, phone_mobile: offer.creator_phone_mobile, phone_office: offer.creator_phone_office },
    ...(collaborators || []).map(c => ({ name: c.name, job_title: c.job_title, email: c.contact_email || c.email, phone_mobile: c.phone_mobile, phone_office: c.phone_office }))
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-xs text-blue-200 uppercase tracking-wide">{t('publicOffer.issueDateLabel')}</div>
            <div className="text-lg font-bold">{new Date(offer.created_at).toLocaleDateString('pl-PL')}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-blue-200 uppercase tracking-wide">{t('publicOffer.offerLabel')}</div>
            <div className="text-lg font-bold">{offer.offer_number}</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleLang} className="text-xs text-blue-200 hover:text-white hover:bg-white/10 px-2 py-1 rounded transition">
              {i18n.language === 'pl' ? 'EN' : 'PL'}
            </button>
            {offer.issuer_logo
              ? <img src={`/uploads/${offer.issuer_logo}`} alt="" className="h-10 max-w-[140px] object-contain" />
              : <div className="text-right text-sm font-bold">{offer.issuer_name || ''}</div>
            }
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Parties */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <div className="text-xs font-bold text-blue-500 uppercase tracking-wide mb-2">{t('publicOffer.issuerLabel')}</div>
            <div className="text-lg font-bold text-gray-800">{issuer}</div>
            <div className="text-sm text-gray-600 mt-1 space-y-0.5">
              {offer.issuer_nip && <div>NIP: {offer.issuer_nip}</div>}
              {issuerAddress && <div>{issuerAddress}</div>}
              {offer.issuer_phone && <div><a href={`tel:${offer.issuer_phone}`} className="text-blue-600 hover:underline">tel. {offer.issuer_phone}</a></div>}
              {offer.issuer_email && <div><a href={`mailto:${offer.issuer_email}`} className="text-blue-600 hover:underline">{offer.issuer_email}</a></div>}
              {offer.issuer_website && <div><a href={offer.issuer_website.startsWith('http') ? offer.issuer_website : 'https://'+offer.issuer_website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{offer.issuer_website}</a></div>}
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{t('publicOffer.clientLabel')}</div>
            <div className="text-lg font-bold text-gray-800">{company}</div>
            <div className="text-sm text-gray-600 mt-1 space-y-0.5">
              {offer.client_name && <div>{offer.client_name}</div>}
              {offer.company_nip && <div>NIP: {offer.company_nip}</div>}
              {offer.company_address && <div>{offer.company_address}</div>}
              {offer.company_email && <div><a href={`mailto:${offer.company_email}`} className="text-blue-600 hover:underline">{offer.company_email}</a></div>}
              {offer.company_phone && <div><a href={`tel:${offer.company_phone}`} className="text-blue-600 hover:underline">tel. {offer.company_phone}</a></div>}
              {offer.address_montage && <div className="font-medium text-gray-700">{t('publicOffer.implementationAddress')}: {offer.address_montage}</div>}
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase font-semibold mb-1">{t('publicOffer.deadlineLabel')}</div>
            <div className="text-base font-bold text-gray-800">{offer.deadline ? new Date(offer.deadline).toLocaleDateString('pl-PL') : '—'}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase font-semibold mb-1">{t('publicOffer.validityLabel')}</div>
            <div className="text-base font-bold text-gray-800">{offer.valid_until ? new Date(offer.valid_until).toLocaleDateString('pl-PL') : '—'}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase font-semibold mb-1">{authors.length > 1 ? t('publicOffer.authors') : t('publicOffer.author')}</div>
            {authors.map((a, i) => (
              <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}>
                <div className="text-sm font-bold text-gray-800">{a.name}{a.job_title ? <span className="font-normal text-gray-500"> — {a.job_title}</span> : ''}</div>
                <div className="text-xs text-gray-500">
                  {a.email && <div><a href={`mailto:${a.email}`} className="text-blue-600 hover:underline">{a.email}</a></div>}
                  {a.phone_mobile && <div><a href={`tel:${a.phone_mobile}`} className="text-blue-600 hover:underline">kom. {a.phone_mobile}</a></div>}
                  {a.phone_office && <div><a href={`tel:${a.phone_office}`} className="text-blue-600 hover:underline">tel. {a.phone_office}</a></div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Offer title */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-4">
          <h2 className="text-lg font-bold text-slate-800">{offer.name}</h2>
          {offer.description && <p className="text-sm text-gray-500 mt-1">{offer.description}</p>}
        </div>

        {/* Configurator parameters */}
        {localVars.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-amber-800 mb-3">{t('publicOffer.configuration', 'Parametry konfiguracji')}</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {localVars.map(v => (
                <div key={v.key} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{v.description || v.key}</span>
                  <span className="font-mono font-semibold text-gray-800">{Number(v.value).toLocaleString('pl-PL')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items table + totals per variant */}
        {visibleVariants.map(variant => {
          const variantItems = items.filter(i => i.variant_id === variant.id);
          const variantTree = buildTree(variantItems);
          const visibleTree = variantTree.filter(n => n.visible !== false);
          if (visibleTree.length === 0) return null;
          const vTotal = variantItems.filter(i => !i.parent_id).reduce((s, i) => s + Number(i.result_value || 0), 0);
          let vNetto, vBrutto, vVat;
          if (priceType === 'netto') {
            vNetto = vTotal; vVat = vTotal * vatRate / 100; vBrutto = vTotal + vVat;
          } else {
            vBrutto = vTotal; vVat = vTotal - vTotal / (1 + vatRate / 100); vNetto = vTotal - vVat;
          }
          return (
            <div key={variant.id} className="space-y-3">
              {visibleVariants.length > 1 && (
                <h3 className="text-sm font-bold text-indigo-700 mt-4">{variant.name}</h3>
              )}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-700 text-white">
                      <th className="text-left py-2.5 px-3 font-semibold text-xs uppercase tracking-wide">{t('publicOffer.position')}</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-xs uppercase tracking-wide w-36">{t('publicOffer.valueColumn', { type: priceType, currency: cur })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTree.map(node => <TreeRow key={node.id} node={node} depth={0} />)}
                  </tbody>
                </table>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {priceType === 'netto' ? (<>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 px-4 text-right font-semibold text-gray-600">{t('publicOffer.totalNetto')}</td>
                        <td className="py-2.5 px-4 text-right font-semibold text-gray-600 w-40">{fmt(vNetto)} {cur}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 px-4 text-right text-gray-500">{t('publicOffer.vatLabel', { rate: vatRate })}</td>
                        <td className="py-2.5 px-4 text-right text-gray-500 w-40">{fmt(vVat)} {cur}</td>
                      </tr>
                      <tr className="bg-gradient-to-r from-slate-800 to-blue-600 text-white">
                        <td className="py-3 px-4 text-right font-bold text-base">{t('publicOffer.totalBrutto')}</td>
                        <td className="py-3 px-4 text-right font-bold text-base w-40">{fmt(vBrutto)} {cur}</td>
                      </tr>
                    </>) : (<>
                      <tr className="bg-gradient-to-r from-slate-800 to-blue-600 text-white">
                        <td className="py-3 px-4 text-right font-bold text-base">{t('publicOffer.totalBrutto')}</td>
                        <td className="py-3 px-4 text-right font-bold text-base w-40">{fmt(vBrutto)} {cur}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 px-4 text-right text-gray-500">{t('publicOffer.vatIncluded', { rate: vatRate })}</td>
                        <td className="py-2.5 px-4 text-right text-gray-500 w-40">{fmt(vVat)} {cur}</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-4 text-right font-semibold text-gray-600">{t('publicOffer.nettoValue')}</td>
                        <td className="py-2.5 px-4 text-right font-semibold text-gray-600 w-40">{fmt(vNetto)} {cur}</td>
                      </tr>
              </>)}
                  </tbody>
                </table>
              </div>
              <ClientConversation token={token} variantId={variant.id} variantName={variant.name} />
            </div>
          );
        })}

        {/* Notes */}
        {offer.extra_notes_items?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">{t('publicOffer.additionalInfo')}</div>
            {offer.extra_notes_items.map((n, i) => (
              <div key={i} className="text-sm text-amber-900 mt-2">
                <span className="font-semibold">{n.title}:</span> {n.content}
              </div>
            ))}
          </div>
        )}

        <div className="text-center text-xs text-gray-400 pt-6 pb-8">
          {t('publicOffer.generatedBy')} <a href="https://swibi.net" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">SWIBI.net</a>
        </div>
      </div>
    </div>
  );
}

function ClientConversation({ token, variantId, variantName = '' }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [clientName, setClientName] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmWord, setConfirmWord] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const r = await api.get(`/offers/public/${token}/variants/${variantId}/messages`);
      setMessages(r.data.messages || []);
    } catch {}
  }, [token, variantId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const handleSend = () => {
    if (!text.trim()) return;
    if (!clientName.trim()) { setError(t('publicOffer.enterYourName')); return; }
    setError('');
    setShowConfirm(true);
    setConfirmWord('');
  };

  const confirmSend = async () => {
    if (confirmWord !== 'OK') { setError(t('publicOffer.typeOkToConfirm')); return; }
    try {
      if (attachment) {
        const formData = new FormData();
        formData.append('content', text);
        formData.append('author_name', clientName);
        formData.append('attachment', attachment);
        await api.post(`/offers/public/${token}/variants/${variantId}/messages`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post(`/offers/public/${token}/variants/${variantId}/messages`, { content: text, author_name: clientName });
      }
      setText('');
      setAttachment(null);
      setShowConfirm(false);
      setConfirmWord('');
      setError('');
      loadMessages();
    } catch (err) { setError(err.response?.data?.error || t('common.error')); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-3">
      <div className="flex items-center justify-between">
        <button onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 font-medium">
          <FileText size={16} />
          {t('offers.conversation', { count: messages.length })}
          {variantName && <span className="text-purple-500 font-normal">{t('offers.regardingVariant', { name: variantName })}</span>}
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {expanded && messages.length > 1 && (
          <button onClick={() => setSortAsc(!sortAsc)} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 border border-gray-200 rounded">
            {sortAsc ? t('common.fromOldest') : t('common.fromNewest')}
          </button>
        )}
      </div>
      {expanded && (() => {
        const messagesList = messages.length > 0 && (
          <div className="space-y-2">
            {[...messages].sort((a, b) => sortAsc ? new Date(a.created_at) - new Date(b.created_at) : new Date(b.created_at) - new Date(a.created_at)).map(m => (
              <div key={m.id} className={`rounded-lg px-4 py-3 text-sm ${m.author_type === 'client' ? 'bg-amber-50 border border-amber-200 ml-6' : 'bg-blue-50 border border-blue-200 mr-6'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-700">{m.author_name}</span>
                  <span className="text-gray-800 text-xs font-bold">{new Date(m.created_at).toLocaleString('pl-PL')}</span>
                </div>
                <div className="text-gray-600 whitespace-pre-wrap">{m.content}</div>
                {m.attachment_url && (
                  <button onClick={() => setLightbox(m.attachment_url)} className="block mt-2 cursor-zoom-in">
                    <img src={m.attachment_url} alt={m.attachment_name || 'Załącznik'} className="max-w-[240px] max-h-[180px] rounded border border-gray-200 hover:opacity-80" />
                  </button>
                )}
              </div>
            ))}
          </div>
        );
        const inputForm = (
          <div>
            {error && <div className="text-red-500 text-xs mb-2">{error}</div>}
            {!showConfirm ? (
              <div className="space-y-2">
                <input type="text" value={clientName} onChange={e => setClientName(e.target.value)}
                  placeholder={t('publicOffer.yourNamePlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <div className="flex gap-2">
                  <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
                    placeholder={t('offers.writeMessage')} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" />
                  <div className="flex flex-col gap-1 self-end">
                    <input type="file" accept="image/*" ref={fileInputRef} className="hidden"
                      onChange={e => { if (e.target.files[0]) setAttachment(e.target.files[0]); }} />
                    <button onClick={() => fileInputRef.current?.click()} type="button"
                      className="p-2 border border-gray-300 hover:bg-gray-100 rounded-lg text-gray-400" title={t('offers.attachPhoto')}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                    </button>
                    <button onClick={handleSend} disabled={!text.trim()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm">
                      {t('offers.sendMessage')}
                    </button>
                  </div>
                </div>
                {attachment && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                    <img src={URL.createObjectURL(attachment)} alt="" className="w-12 h-12 object-cover rounded" />
                    <span className="truncate">{attachment.name}</span>
                    <button onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="text-red-400 hover:text-red-600 ml-auto text-lg">&times;</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-3">
                <div className="text-sm text-amber-800">{t('offers.confirmWarning')}</div>
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 whitespace-pre-wrap">
                  {text}
                  {attachment && <img src={URL.createObjectURL(attachment)} alt="" className="mt-2 max-w-[200px] max-h-[150px] rounded border" />}
                </div>
                <div className="text-sm text-amber-800">{t('offers.confirmTypeOk')}</div>
                <div className="flex gap-2 items-center">
                  <input type="text" value={confirmWord} onChange={e => setConfirmWord(e.target.value)}
                    placeholder="OK" className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm font-bold text-center"
                    onKeyDown={e => { if (e.key === 'Enter') confirmSend(); }} autoFocus />
                  <button onClick={confirmSend} disabled={confirmWord !== 'OK'}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded-lg text-sm">{t('common.confirm')}</button>
                  <button onClick={() => { setShowConfirm(false); setConfirmWord(''); }}
                    className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm">{t('common.cancel')}</button>
                </div>
              </div>
            )}
          </div>
        );
        return (
          <div className="mt-3 space-y-3">
            {sortAsc ? <>{messagesList}{inputForm}</> : <>{inputForm}{messagesList}</>}
          </div>
        );
      })()}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
